import React, { useEffect, useState } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { apiGateway, GW } from '../config/apiGateway';
import {
  getLeadSaveMessage,
  getLeadInsertedId,
  isLeadSaveSuccess,
} from '../config/taskApi';
import './landing-apply-form.css';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PINCODE_REGEX = /^[1-9]\d{5}$/;
const ONLY_DIGITS = /^\d+$/;

const MIN_LOAN = 10000;
const MAX_LOAN = 100000;
const MIN_INCOME = 50000;
const MAX_INCOME = 5000000;

const initialForm = {
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  pan: '',
  loanAmount: '',
  monthlyIncome: '',
  state: '',
  city: '',
  pincode: '',
  address: '',
  termsAccepted: false,
};

const validate = (form, stateId, cityId) => {
  const errors = {};

  if (!form.firstName.trim()) errors.firstName = 'First name is required';
  if (!form.lastName.trim()) errors.lastName = 'Last name is required';

  if (!MOBILE_REGEX.test(form.mobile.trim())) {
    errors.mobile = 'Enter valid 10-digit mobile';
  }

  if (!EMAIL_REGEX.test(form.email.trim())) {
    errors.email = 'Enter valid email';
  }

  const pan = form.pan.trim().toUpperCase();
  if (!pan) errors.pan = 'PAN is required';
  else if (!PAN_REGEX.test(pan)) errors.pan = 'Enter valid PAN (e.g. ABCDE1234F)';

  if (!ONLY_DIGITS.test(form.loanAmount.trim())) {
    errors.loanAmount = 'Enter loan amount';
  } else {
    const loan = Number(form.loanAmount);
    if (loan < MIN_LOAN || loan > MAX_LOAN) {
      errors.loanAmount = `Loan must be ₹${MIN_LOAN.toLocaleString('en-IN')} – ₹${MAX_LOAN.toLocaleString('en-IN')}`;
    }
  }

  if (!ONLY_DIGITS.test(form.monthlyIncome.trim())) {
    errors.monthlyIncome = 'Enter monthly income';
  } else {
    const income = Number(form.monthlyIncome);
    if (income < MIN_INCOME || income > MAX_INCOME) {
      errors.monthlyIncome = `Income must be at least ₹${MIN_INCOME.toLocaleString('en-IN')}`;
    }
  }

  if (!stateId) errors.state = 'Select state';
  if (!cityId) errors.city = 'Select city';
  if (!PINCODE_REGEX.test(form.pincode.trim())) errors.pincode = 'Enter valid pincode';

  if (form.address.trim().length < 15) {
    errors.address = 'Address must be at least 15 characters';
  }

  if (!form.termsAccepted) errors.termsAccepted = 'Please accept terms';

  return errors;
};

const LandingApplyForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [stateId, setStateId] = useState('');
  const [cityId, setCityId] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [refNo, setRefNo] = useState('');

  useEffect(() => {
    apiGateway(GW.GET_STATE)
      .then((res) => {
        if (res.data?.Status === 1) setStates(res.data.Data || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!stateId) {
      setCities([]);
      setCityId('');
      setPincodes([]);
      return;
    }

    setLoadingCities(true);
    apiGateway(GW.GET_CITY, { state_id: Number(stateId) })
      .then((res) => {
        if (res.data?.Status === 1) setCities(res.data.Data || []);
        else setCities([]);
      })
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [stateId]);

  useEffect(() => {
    if (!cityId) {
      setPincodes([]);
      return;
    }

    setLoadingPincodes(true);
    apiGateway(GW.GET_PINCODE, { city_id: Number(cityId) })
      .then((res) => {
        if (res.data?.Status === 1) setPincodes(res.data.Data || []);
        else setPincodes([]);
      })
      .catch(() => setPincodes([]))
      .finally(() => setLoadingPincodes(false));
  }, [cityId]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setSubmitError('');
  };

  const handleStateChange = (e) => {
    const sid = e.target.value;
    const selected = states.find((s) => String(s.state_id) === sid);
    setStateId(sid);
    setCityId('');
    setForm((prev) => ({ ...prev, state: selected?.state || '', city: '', pincode: '' }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.state;
      delete next.city;
      delete next.pincode;
      return next;
    });
  };

  const handleCityChange = (e) => {
    const cid = e.target.value;
    const selected = cities.find((c) => String(c.city_id) === cid);
    setCityId(cid);
    setForm((prev) => ({ ...prev, city: selected?.city || '', pincode: '' }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.city;
      delete next.pincode;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate(form, stateId, cityId);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    const pan = form.pan.trim().toUpperCase();
    const payload = {
      loan_amount: Number(form.loanAmount),
      monthly_income: String(form.monthlyIncome).trim(),
      first_name: form.firstName.trim(),
      middle_name: '',
      sur_name: form.lastName.trim(),
      pan,
      pan_status: 0,
      pan_responseCode: '',
      pan_responseMessage: '',
      pan_referenceId: '',
      pan_firstName: '',
      pan_lastName: '',
      pan_address: '',
      pan_gender: '',
      pan_aadhaarLinked: '',
      pan_dob: '',
      pan_fullName: '',
      pan_maskedAadhaarNumber: '',
      pan_category: '',
      pan_verifiedPan: '',
      dob: '',
      gender: '',
      mobile: form.mobile.trim(),
      alternate_mobile: '',
      email_personal: form.email.trim(),
      email_official: '',
      obligations: 0,
      state_id: Number(stateId),
      city: String(cityId),
      city_id: Number(cityId),
      pin: form.pincode.trim(),
      pincode: form.pincode.trim(),
      address: form.address.trim(),
      term_and_condition: 'YES',
      ip: '',
      coordinates: '',
      source: 'MrRupya_Landing',
      lead_data_source_id: 39,
      utm_source: '',
      utm_campaign: '',
      selfie_image_url: '',
      aadhaar_number: '',
      aadhaar_last_four: '',
    };

    try {
      const resp = await apiGateway(GW.SAVE_LEAD, payload, { timeout: 30000 });

      if (isLeadSaveSuccess(resp.data)) {
        const id = getLeadInsertedId(resp.data);
        setRefNo(id ? `MR${id}` : `MR${Date.now().toString().slice(-8)}`);
        setSubmitted(true);
        return;
      }

      setSubmitError(getLeadSaveMessage(resp?.data, 'Submit failed. Please try again.'));
    } catch (err) {
      setSubmitError(
        getLeadSaveMessage(err.response?.data, err.message || 'Network error. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="lp-form-card lp-form-success">
        <div className="lp-success-icon">
          <FiCheckCircle />
        </div>
        <h3>Application Received!</h3>
        <p>
          Thank you, {form.firstName}! Our team will contact you on{' '}
          <strong>+91 {form.mobile}</strong> shortly.
        </p>
        {refNo && (
          <p className="lp-success-ref">
            Reference: <strong>{refNo}</strong>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="lp-form-card">
      <div className="lp-form-header">
        <h2 className="lp-form-title">Apply for Loan</h2>
      </div>

      <form className="lp-form" onSubmit={handleSubmit} noValidate>
        <div className="lp-form-grid">
          <div className={`lp-field ${errors.firstName ? 'has-error' : ''}`}>
            <label htmlFor="lp-firstName">First Name *</label>
            <input
              id="lp-firstName"
              value={form.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              placeholder="First name"
              autoComplete="given-name"
            />
            {errors.firstName && <span className="lp-error">{errors.firstName}</span>}
          </div>

          <div className={`lp-field ${errors.lastName ? 'has-error' : ''}`}>
            <label htmlFor="lp-lastName">Last Name *</label>
            <input
              id="lp-lastName"
              value={form.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              placeholder="Last name"
              autoComplete="family-name"
            />
            {errors.lastName && <span className="lp-error">{errors.lastName}</span>}
          </div>

          <div className={`lp-field ${errors.mobile ? 'has-error' : ''}`}>
            <label htmlFor="lp-mobile">Mobile *</label>
            <input
              id="lp-mobile"
              value={form.mobile}
              onChange={(e) =>
                updateField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))
              }
              placeholder="10-digit mobile"
              inputMode="numeric"
              autoComplete="tel-national"
            />
            {errors.mobile && <span className="lp-error">{errors.mobile}</span>}
          </div>

          <div className={`lp-field ${errors.email ? 'has-error' : ''}`}>
            <label htmlFor="lp-email">Email *</label>
            <input
              id="lp-email"
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="name@email.com"
              autoComplete="email"
            />
            {errors.email && <span className="lp-error">{errors.email}</span>}
          </div>

          <div className={`lp-field ${errors.pan ? 'has-error' : ''}`}>
            <label htmlFor="lp-pan">PAN *</label>
            <input
              id="lp-pan"
              value={form.pan}
              onChange={(e) =>
                updateField('pan', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))
              }
              placeholder="ABCDE1234F"
              autoComplete="off"
            />
            {errors.pan && <span className="lp-error">{errors.pan}</span>}
          </div>

          <div className={`lp-field ${errors.loanAmount ? 'has-error' : ''}`}>
            <label htmlFor="lp-loan">Loan Amount *</label>
            <input
              id="lp-loan"
              value={form.loanAmount}
              onChange={(e) =>
                updateField('loanAmount', e.target.value.replace(/\D/g, '').slice(0, 7))
              }
              placeholder="₹10,000 – ₹1,00,000"
              inputMode="numeric"
            />
            {errors.loanAmount && <span className="lp-error">{errors.loanAmount}</span>}
          </div>

          <div className={`lp-field ${errors.monthlyIncome ? 'has-error' : ''}`}>
            <label htmlFor="lp-income">Monthly Income *</label>
            <input
              id="lp-income"
              value={form.monthlyIncome}
              onChange={(e) =>
                updateField('monthlyIncome', e.target.value.replace(/\D/g, '').slice(0, 8))
              }
              placeholder="Min ₹50,000"
              inputMode="numeric"
            />
            {errors.monthlyIncome && <span className="lp-error">{errors.monthlyIncome}</span>}
          </div>

          <div className={`lp-field ${errors.state ? 'has-error' : ''}`}>
            <label htmlFor="lp-state">State *</label>
            <select id="lp-state" value={stateId} onChange={handleStateChange}>
              <option value="">Select state</option>
              {states.map((s) => (
                <option key={s.state_id} value={String(s.state_id)}>
                  {s.state}
                </option>
              ))}
            </select>
            {errors.state && <span className="lp-error">{errors.state}</span>}
          </div>

          <div className={`lp-field ${errors.city ? 'has-error' : ''}`}>
            <label htmlFor="lp-city">City *</label>
            <select
              id="lp-city"
              value={cityId}
              onChange={handleCityChange}
              disabled={!stateId || loadingCities}
            >
              <option value="">
                {!stateId ? 'Select state first' : loadingCities ? 'Loading...' : 'Select city'}
              </option>
              {cities.map((c) => (
                <option key={c.city_id} value={String(c.city_id)}>
                  {c.city}
                </option>
              ))}
            </select>
            {errors.city && <span className="lp-error">{errors.city}</span>}
          </div>

          <div className={`lp-field ${errors.pincode ? 'has-error' : ''}`}>
            <label htmlFor="lp-pincode">Pincode *</label>
            <select
              id="lp-pincode"
              value={form.pincode}
              onChange={(e) => updateField('pincode', e.target.value)}
              disabled={!cityId || loadingPincodes || pincodes.length === 0}
            >
              <option value="">
                {!cityId
                  ? 'Select city first'
                  : loadingPincodes
                    ? 'Loading...'
                    : pincodes.length
                      ? 'Select pincode'
                      : 'No pincodes'}
              </option>
              {pincodes.map((p) => (
                <option key={p.pincode_id || p.m_pincode_value} value={String(p.m_pincode_value)}>
                  {p.m_pincode_value}
                </option>
              ))}
            </select>
            {errors.pincode && <span className="lp-error">{errors.pincode}</span>}
          </div>

          <div className={`lp-field lp-field-full ${errors.address ? 'has-error' : ''}`}>
            <label htmlFor="lp-address">Address *</label>
            <textarea
              id="lp-address"
              value={form.address}
              onChange={(e) => updateField('address', e.target.value.slice(0, 500))}
              placeholder="House no., street, area"
              rows={2}
            />
            {errors.address && <span className="lp-error">{errors.address}</span>}
          </div>
        </div>

        <label className={`lp-terms ${errors.termsAccepted ? 'has-error' : ''}`}>
          <input
            type="checkbox"
            checked={form.termsAccepted}
            onChange={(e) => updateField('termsAccepted', e.target.checked)}
          />
          <span>
            I agree to the{' '}
            <a href="/terms-conditions" target="_blank" rel="noopener noreferrer">
              Terms &amp; Conditions
            </a>{' '}
            and authorize MR Rupya to contact me.
          </span>
        </label>
        {errors.termsAccepted && <span className="lp-error">{errors.termsAccepted}</span>}

        {submitError && <div className="lp-submit-error">{submitError}</div>}

        <button type="submit" className="lp-submit-btn" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default LandingApplyForm;
